<template>
    <div class="markdown-parameter-toolbar">
        <button
            ref="buttonRef"
            class="parameter-insert-btn"
            type="button"
            @click="toggleDropdown('parameters')"
        >
            <span class="parameter-insert-btn__icon">{}</span>
            <span>{{ label }}</span>
            <span class="parameter-insert-btn__arrow">
                <RedesignBaseIcon name="ChevronDown" />
            </span>
        </button>

        <!-- Templates Button -->
        <button
            v-if="templates.length || isLoadingTemplates"
            ref="templateButtonRef"
            class="parameter-insert-btn"
            type="button"
            @click="toggleDropdown('templates')"
        >
            <RedesignBaseIcon
                name="FileText"
                class="w-4 h-4"
            />
            <span>{{ $t('widget.ai.templates') }}</span>
            <span class="parameter-insert-btn__arrow">
                <RedesignBaseIcon name="ChevronDown" />
            </span>
        </button>

        <!-- Parameter Dropdown -->
        <div
            v-if="isOpen && activeDropdown === 'parameters'"
            ref="dropdownRef"
            class="parameter-dropdown"
        >
            <div class="parameter-dropdown__content">
                <div
                    v-for="group in parameterGroups"
                    :key="group.label"
                    class="parameter-group"
                >
                    <div
                        class="parameter-group__header"
                        @click="toggleGroup(group.label)"
                    >
                        <span>{{ group.label }}</span>
                        <span class="parameter-group__arrow">
                            <RedesignBaseIcon
                                v-if="expandedGroups.includes(group.label)"
                                name="ChevronDown"
                            />
                            <RedesignBaseIcon
                                v-else
                                name="ChevronRight"
                            />
                        </span>
                    </div>
                    <div
                        v-if="expandedGroups.includes(group.label)"
                        class="parameter-group__items"
                    >
                        <div
                            v-for="param in group.options"
                            :key="param.value"
                            class="parameter-item-wrapper"
                        >
                            <!-- Using RedesignBasePopover for submenu -->
                            <RedesignBasePopover
                                trigger="click"
                                placement="right-start"
                                :visible-arrow="false"
                                popper-class="parameter-options-popover"
                            >
                                <template #reference>
                                    <div class="parameter-item">
                                        <span>{{ param.label }}</span>
                                        <span class="parameter-item__arrow">
                                            <RedesignBaseIcon name="ChevronRight" />
                                        </span>
                                    </div>
                                </template>

                                <!-- Submenu options -->
                                <div class="parameter-options">
                                    <div
                                        class="parameter-option"
                                        @click="selectParameter(param.value, 'value')"
                                    >
                                        <span
                                            class="parameter-option__icon"
                                            v-text="'{{ }}'"
                                        />
                                        {{ $t('widget.ai.paramType.value') }}
                                    </div>
                                    <div
                                        class="parameter-option"
                                        @click="selectParameter(param.value, 'openIf')"
                                    >
                                        <span
                                            class="parameter-option__icon"
                                            v-text="'{{#}}'"
                                        />
                                        {{ $t('widget.ai.paramType.openIf') }}
                                    </div>
                                    <div
                                        class="parameter-option"
                                        @click="selectParameter(param.value, 'closeIf')"
                                    >
                                        <span
                                            class="parameter-option__icon"
                                            v-text="'{{/}}'"
                                        />
                                        {{ $t('widget.ai.paramType.closeIf') }}
                                    </div>
                                </div>
                            </RedesignBasePopover>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Templates Dropdown -->
        <div
            v-if="isOpen && activeDropdown === 'templates'"
            ref="templateDropdownRef"
            class="parameter-dropdown template-dropdown"
        >
            <div
                v-if="isLoadingTemplates"
                class="flex items-center justify-center py-4"
            >
                <span class="text-gray-500 text-xs">{{ $t('common.loading') }}...</span>
            </div>
            <div
                v-else
                class="parameter-dropdown__content"
            >
                <div
                    v-for="template in templates"
                    :key="template.TemplateID"
                    class="template-item"
                    @click="selectTemplate(template)"
                >
                    <span>{{ template.Name }}</span>
                </div>
                <div
                    v-if="templates.length === 0"
                    class="text-gray-500 text-xs px-3 py-2"
                >
                    {{ $t('widget.ai.noTemplates') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { buildParameterPlaceholder, type ParameterType } from '@/composables/useMarkdownWidgets'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import type { AiTemplate } from '@/api/widgetParameterApi'
import { TranslateResult } from 'vue-i18n'

export interface ParameterOption {
    label: string
    value: string
}

export interface ParameterGroup {
    label: string
    options: ParameterOption[]
}

export interface TemplateItem {
    TemplateID: number
    Name: string
    Template: string
}

type DropdownType = 'parameters' | 'templates'

const props = withDefaults(defineProps<{
    parameterGroups: ParameterGroup[]
    templates?: AiTemplate[]
    isLoadingTemplates?: boolean
    label?: string | TranslateResult
}>(), {
    label: 'Insert Parameter',
    templates: () => [],
    isLoadingTemplates: false
})

const emit = defineEmits<{
    (e: 'insert', text: string): void
    (e: 'select-template', template: AiTemplate): void
}>()

// Refs
const buttonRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const templateButtonRef = ref<HTMLElement | null>(null)
const templateDropdownRef = ref<HTMLElement | null>(null)

// State
const isOpen = ref(false)
const activeDropdown = ref<DropdownType>('parameters')
const expandedGroups = ref<string[]>([])

// Methods
function toggleDropdown (type: DropdownType) {
    if (isOpen.value && activeDropdown.value === type) {
        // Close if same dropdown clicked
        isOpen.value = false
        resetState()
    } else {
        // Open or switch dropdown
        activeDropdown.value = type
        isOpen.value = true
        expandedGroups.value = []
    }
}

function toggleGroup (groupLabel: string) {
    const index = expandedGroups.value.indexOf(groupLabel)
    if (index > -1) {
        expandedGroups.value.splice(index, 1)
    } else {
        expandedGroups.value.push(groupLabel)
    }
}

function selectParameter (paramValue: string, type: ParameterType) {
    const placeholder = buildParameterPlaceholder(paramValue, type)
    emit('insert', placeholder)
    isOpen.value = false
    resetState()
}

function selectTemplate (template: AiTemplate) {
    emit('select-template', template)
    isOpen.value = false
    resetState()
}

function resetState () {
    expandedGroups.value = []
    activeDropdown.value = 'parameters'
}

function handleClickOutside (event: MouseEvent) {
    if (!isOpen.value) return

    const target = event.target as Node
    const isInsideDropdown = dropdownRef.value?.contains(target)
    const isInsideButton = buttonRef.value?.contains(target)
    const isInsideTemplateDropdown = templateDropdownRef.value?.contains(target)
    const isInsideTemplateButton = templateButtonRef.value?.contains(target)
    // Check if click is inside a popover (they are appended to body)
    const isInsidePopover = (event.target as HTMLElement)?.closest('.parameter-options-popover')

    if (!isInsideDropdown && !isInsideButton && !isInsideTemplateDropdown && !isInsideTemplateButton && !isInsidePopover) {
        isOpen.value = false
        resetState()
    }
}

// Lifecycle
onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.markdown-parameter-toolbar {
    @apply relative inline-flex items-center gap-2;
}

.parameter-insert-btn {
    @apply flex items-center gap-2 font-medium text-primary cursor-pointer;
    padding: 6px 12px;
    background: var(--mainBgColor-color);
    border: 1px solid var(--primary-actions);
    border-radius: 4px;
    font-size: 13px;
    transition: all 0.15s;

    &:hover {
        border-color: var(--primary-actions);
        background: color-mix(
                in srgb,
                var(--backgroundLight-color) 95%,
                var(--primary-color) 5%
        );
    }
}

.parameter-dropdown {
    @apply absolute overflow-y-auto mt-0.5;
    top: 100%;
    left: 0;
    z-index: 1000;
    min-width: 250px;
    max-height: 300px;
    background: var(--mainBgColor-color);
    border: 1px solid var(--field-borders);
    border-radius: 4px;

    &__content {
        @apply py-1;
    }
}

.parameter-group {
    &__header {
        @apply
        flex
        justify-between
        items-center
        font-medium
        text-primary
        cursor-pointer
        py-2
        px-3;
        transition: background 0.15s;

        &:hover {
            background: color-mix(
                    in srgb,
                    var(--backgroundLight-color) 90%,
                    var(--primary-color) 10%
            );
        }
    }

    &__items {
        background: #fafafa;
        border-top: 1px solid #eee;
    }
}

.parameter-item {
    @apply flex justify-between items-center text-xs cursor-pointer;
    padding: 8px 12px 8px 24px;
    transition: all 0.15s;

    &:hover {
        background: var(--primary-actions);
        color: white;
    }
}

.parameter-options {
    min-width: 120px;
}

.template-dropdown {
    min-width: 280px;
}

.template-item {
    @apply flex items-center text-sm cursor-pointer px-3 py-2;
    transition: all 0.15s;

    &:hover {
        background: var(--primary-actions);
        color: white;
    }
}

.parameter-option {
    @apply flex items-center gap-2 cursor-pointer px-3 py-2 text-xs;

    &:hover {
        background: var(--primary-actions);
        color: white;

        .parameter-option__icon {
            color: white;
        }
    }

    &__icon {
        @apply font-medium text-xs text-primary;
    }
}
</style>

<!-- Global styles for the popover (it's appended to body) -->
<style lang="scss">
.el-popover.parameter-options-popover {
    padding: 4px 0 !important;
    min-width: 120px !important;
    background: var(--mainBgColor-color);
    border: 1px solid var(--field-borders);
    border-radius: 4px;
}
</style>
